import React, { useState, useEffect, useMemo } from 'react';
import Core from '../../../common/clientSdk';
import Utils from '../../../common/utils';
import ContentCard from './ContentCard';
import { FallBackBtn, ShimmerBox } from '../../../components';

// Shimmer loading card for content
const ShimmerContentCard = () => (
    <div className="content-grid-card" style={{ padding: 0, flex: '0 0 320px', width: '320px', maxWidth: '320px', height: '420px' }}>
        {/* Card Header Shimmer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ShimmerBox width="36px" height="36px" borderRadius="50%" />
                <ShimmerBox width="100px" height="14px" />
            </div>
            <ShimmerBox width="28px" height="28px" borderRadius="50%" />
        </div>
        {/* Card Image Shimmer */}
        <div style={{ flex: '1', width: '100%', overflow: 'hidden' }}>
            <ShimmerBox width="100%" height="100%" borderRadius="0" />
        </div>
        {/* Card Footer Shimmer */}
        <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
                <ShimmerBox width="60px" height="22px" borderRadius="12px" />
                <ShimmerBox width="80px" height="22px" borderRadius="12px" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <ShimmerBox width="50px" height="16px" />
                <ShimmerBox width="50px" height="16px" />
                <ShimmerBox width="50px" height="16px" />
                <ShimmerBox width="50px" height="16px" />
            </div>
        </div>
    </div>
);

// Shimmer loading for content section
const ShimmerContentSection = () => (
    <div className="content-cards-scroll">
        {[...Array(4)].map((_, i) => <ShimmerContentCard key={i} />)}
    </div>
);

const DEFAULT_PROFILE_PIC = "/assets/images/default-profile.png";

const buildInfluencerProfilePictureUrl = (pictureValue, influencerId) => {
    if (!pictureValue || typeof pictureValue !== 'string') return null;
    if (pictureValue.startsWith('http')) {
        return pictureValue;
    }
    if (influencerId) {
        return Utils.generateInfluencerDpUrl(influencerId, pictureValue);
    }
    return null;
};

const resolveInfluencerProfilePicture = (influencerData, influencerId) => {
    if (!influencerData) return null;
    const candidates = [influencerData.profile_picture, influencerData.influencer_profile_picture];
    for (const candidate of candidates) {
        const resolved = buildInfluencerProfilePictureUrl(candidate, influencerId);
        if (resolved) {
            return resolved;
        }
    }
    return null;
};

const resolveAvatarFromPayload = (item, influencerId) => {
    const influencerData = item?.influencer_data || {};
    return (
        resolveInfluencerProfilePicture(influencerData, influencerId) ||
        buildInfluencerProfilePictureUrl(item?.influencer_profile_picture, influencerId) ||
        buildInfluencerProfilePictureUrl(item?.profile_picture, influencerId) ||
        DEFAULT_PROFILE_PIC
    );
};

/**
 * TopPerformersSection - Top content cards sorted by views and engagement rate
 * @param {object} sectionRef - Ref for scroll navigation
 * @param {array} allContentCards - All content cards data
 */
const TopPerformersSection = ({ sectionRef, allContentCards = [], isVisible, campaignId, is_public, shareKey, isReportGenerated = true, influencerList = [], isInitialLoading, isSharedView }) => {
    const [contentByViews, setContentByViews] = useState([]);
    const [contentByER, setContentByER] = useState([]);
    const isLoadingViews = isInitialLoading;
    const isLoadingER = isInitialLoading;

    // Process influencerList into flattened content items
    const allProcessedContent = useMemo(() => {
        const processed = [];
        (influencerList || []).forEach(item => {
            const contentAnalytics = item?.content_analytics || {};
            const deepData = item?.contents_deep_data || {};
            const influencerData = item?.influencer_data || {};
            const platformsData = item?.platforms_data || {};

            // Collect all unique content keys
            const contentKeys = new Set([
                ...Object.keys(contentAnalytics),
                ...Object.keys(deepData)
            ]);

            contentKeys.forEach(key => {
                const analytics = contentAnalytics[key] || {};
                const deep = deepData[key] || {};
                const contentData = deep.content_data || {};

                // Resolve Profile Pic
                const resolvedProfilePic = resolveAvatarFromPayload(item, influencerData?.influencer_id);

                // Resolve Thumbnail
                const thumbnail = contentData?.media_id?.startsWith('http')
                    ? contentData.media_id
                    : (contentData?.thumbnail_url || deep?.thumbnail_url || "/assets/images/no-img-found.webp");

                processed.push({
                    ...item,
                    ...analytics, // spread analytics (view_count, like_count etc)
                    content_id: key,
                    influencer_data: influencerData,
                    platforms_data: platformsData,
                    platform_id: Array.isArray(platformsData) ? platformsData[0]?.platform_id : platformsData?.platform_id,
                    platformId: Array.isArray(platformsData) ? platformsData[0]?.platform_id : platformsData?.platform_id,

                    // Specific fields for ContentCard
                    owner_name: influencerData?.name || item?.name || "Influencer",
                    owner_pic: resolvedProfilePic,
                    profilePic: resolvedProfilePic,
                    thumbnail: thumbnail,

                    views: analytics.view_count || analytics.views || 0,
                    engagementRate: analytics.engagement_rate || 0,

                    // Ensure content_type_id and other fields are present
                    content_type_id: analytics.content_type_id || contentData.content_type_id,
                    url: contentData.url, // Might be undefined if deep_data is missing
                    shortcode: analytics.shortcode || contentData.shortcode,
                });
            });
        });
        return processed;
    }, [influencerList]);

    useEffect(() => {
        // Sort by Views
        const byViews = [...allProcessedContent]
            .sort((a, b) => (b.views || 0) - (a.views || 0))
            .slice(0, 6);
        setContentByViews(byViews);

        // Sort by Engagement Rate
        const byER = [...allProcessedContent]
            .sort((a, b) => (b.engagementRate || 0) - (a.engagementRate || 0))
            .slice(0, 6);
        setContentByER(byER);

    }, [allProcessedContent]);

    // Using ContentCard for display instead of custom card markup

    return (
        <div className="top-performers-tab section-content" ref={sectionRef}>
            <div className="section-header">
                <h3>Top Content</h3>
                {!is_public && !isSharedView && isVisible !== undefined && (
                    <div className="section-controls sharing-status-container">
                        <span className="sharing-status">Sharing status</span>
                        {isVisible ? (
                            <span className="visible-badge">Visible</span>
                        ) : (
                            <span className="hidden-badge">Hidden</span>
                        )}
                    </div>
                )}
            </div>

            {/* By Views */}
            {!isReportGenerated ? (
                <div style={{ padding: '40px', textAlign: 'center', borderRadius: '12px', minHeight: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', border: '1px dashed #e5e7eb', margin: '20px 0' }}>
                    <h3 style={{ fontSize: '16px', color: '#6B7280', fontWeight: 600 }}>Report not generated yet</h3>
                    <p style={{ color: '#9CA3AF', marginTop: '8px', fontSize: '14px' }}>Click 'Generate Report' to view this data.</p>
                </div>
            ) : (
                <>
                    <div className="top-content-section">
                        <h4 className="top-content-subtitle">By Views</h4>
                        {isLoadingViews ? (
                            <ShimmerContentSection />
                        ) : (
                        <div className="content-cards-scroll">
                            {contentByViews.length > 0 ? (
                                contentByViews.map((item, index) => (
                                    <ContentCard key={item.content_id || index} item={item} />
                                ))
                            ) : (
                                <div className="no-content-message" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    <div className="fall-back-center-campaign" style={{ width: '100%', maxWidth: 520 }}>
                                        <FallBackBtn
                                            heading={"No Content Available"}
                                            title={"Once influencer content is available it will show here."}
                                            widthIcon={100}
                                            heightIcon={100}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        )}
                    </div>

                    {/* By Engagement Rate */}
                    <div className="top-content-section">
                        <h4 className="top-content-subtitle">By Engagement Rate</h4>
                        {isLoadingER ? (
                            <ShimmerContentSection />
                        ) : (
                        <div className="content-cards-scroll">
                            {contentByER.length > 0 ? (
                                contentByER.map((item, index) => (
                                    <ContentCard key={item.content_id || index} item={item} />
                                ))
                            ) : (
                                <div className="no-content-message" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    <div className="fall-back-center-campaign" style={{ width: '100%', maxWidth: 520 }}>
                                        <FallBackBtn
                                            heading={"No Content Available"}
                                            title={"Once influencer content is available it will show here."}
                                            widthIcon={100}
                                            heightIcon={100}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        )}
                    </div>
                </>
            )}
            {/* Styles: make cards same size as ContentSection and enable horizontal scroll when overflow */}
            <style>{`
                .top-content-section .content-cards-scroll {
                    display: flex;
                    gap: 16px;
                    overflow-x: auto;
                    width: 100%;
                    max-width: 100%;
                    -webkit-overflow-scrolling: touch;
                    padding-bottom: 8px;
                    box-sizing: border-box;
                }
                .top-content-section .content-cards-scroll::-webkit-scrollbar {
                    height: 8px;
                }
                .top-content-section .content-cards-scroll::-webkit-scrollbar-thumb {
                    background: rgba(0,0,0,0.08);
                    border-radius: 4px;
                }
                .top-content-section .content-cards-scroll .content-grid-card {
                    flex: 0 0 320px;
                    width: 320px;
                    max-width: 320px;
                    min-width: 320px;
                }
                @media (max-width: 900px) {
                    .top-content-section .content-cards-scroll .content-grid-card {
                        flex: 0 0 280px;
                        width: 280px;
                        max-width: 280px;
                        min-width: 280px;
                    }
                }
                @media (max-width: 480px) {
                    .top-content-section .content-cards-scroll .content-grid-card {
                        flex: 0 0 240px;
                        width: 240px;
                        max-width: 240px;
                        min-width: 240px;
                    }
                }
            `}</style>
        </div>
    );
};

export default TopPerformersSection;
